// 首页「AI 员工 · 今日值班」卡片区：三张员工卡 + 就地追问 + 语音 + 团队会诊
// 一句话播报由后端 /api/dashboard/workbench.briefings 拼装（不调 DeepSeek）；只有点提问才走 /api/{base}/chat
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Input, Button, Tag, Spin, Empty } from 'antd';
import { PhoneOutlined, SendOutlined, TeamOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import VoiceCallModal from './VoiceCallModal.jsx';
import tinaAvatar from '../assets/tina-avatar.jpg';
import heboAvatar from '../assets/hebo-avatar.jpg';
import xiaoliAvatar from '../assets/xiaoli-avatar.jpg';

const STAFF = [
  { key: 'tina', name: '缇娜', role: '仓库管家', apiBase: '/api/tina', accent: '#7F77DD', soft: '#EEEDFE', avatar: tinaAvatar, chips: ['该补什么货', '滞销分析'], route: '/tina' },
  { key: 'hebo', name: '河伯', role: '运营军师', apiBase: '/api/hebo', accent: '#1F5C99', soft: '#E6F1FB', avatar: heboAvatar, chips: ['爆款测算', '竞品点评'], route: '/hebo' },
  { key: 'xiaoli', name: '小李', role: '财务顾问', apiBase: '/api/xiaoli', accent: '#8B6914', soft: '#FAEEDA', avatar: xiaoliAvatar, chips: ['本月赚多少', '税务测算'], route: '/xiaoli' },
];

const chatOnce = async (apiBase, message) => {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), 120000);
  try {
    const res = await apiFetch(apiBase + '/chat', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
      signal: ac.signal,
    }).then(r => r.json());
    return res;
  } finally {
    clearTimeout(timer);
  }
};

function StaffCard({ staff, briefing }) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState([]); // [{role, content}]
  const [loading, setLoading] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const boxRef = useRef(null);
  const { name, role, accent, soft, avatar, chips, route, apiBase } = staff;

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs, loading]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setMsgs(prev => [...prev, { role: 'user', content: q }]);
    setInput(''); setLoading(true);
    try {
      const res = await chatOnce(apiBase, q);
      if (res.success) {
        let content = res.reply;
        if (res.pending) content += '（我已拟好一个操作方案，去「完整对话」里确认执行）';
        setMsgs(prev => [...prev, { role: 'assistant', content }]);
      } else {
        setMsgs(prev => [...prev, { role: 'assistant', content: '⚠️ ' + (res.error || name + '服务异常，请稍后再试') }]);
      }
    } catch (e) {
      setMsgs(prev => [...prev, { role: 'assistant', content: '⚠️ ' + (e && e.name === 'AbortError' ? name + '思考时间过长，请换个更具体的问题' : '网络错误，请稍后再试') }]);
    }
    setLoading(false);
  };

  return (
    <div className="staff-card" style={{ '--accent': accent, '--soft': soft, border: '1px solid #EFE7E0', background: '#FFFFFF', borderRadius: 12, padding: '16px 16px 12px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src={avatar} alt={name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid ' + accent, flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#26201C', lineHeight: 1.2 }}>{name}</div>
          <Tag style={{ margin: '2px 0 0', fontSize: 11, color: accent, background: soft, border: 'none', lineHeight: '16px' }}>{role}</Tag>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
          <Button size="small" shape="circle" title="语音通话" icon={<PhoneOutlined />} onClick={() => setCallOpen(true)} style={{ color: accent, borderColor: '#EFE7E0' }} />
          <Button size="small" type="link" style={{ padding: 0 }} onClick={() => navigate(route)}>完整对话<ArrowRightOutlined /></Button>
        </div>
      </div>

      <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: soft, fontSize: 12.5, color: '#5F5E5A', lineHeight: 1.5, minHeight: 36 }}>
        {briefing || '今日暂无播报'}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {chips.map(c => (
          <button key={c} onClick={() => send(c)} disabled={loading}
            style={{ cursor: 'pointer', border: '1px solid #EFE7E0', background: '#FBF7F3', color: '#5F5E5A', borderRadius: 14, padding: '3px 12px', fontSize: 12, lineHeight: '18px' }}>
            {c}
          </button>
        ))}
      </div>

      {msgs.length > 0 && (
        <div ref={boxRef} style={{ marginTop: 10, maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%', padding: '6px 10px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.45,
              background: m.role === 'user' ? accent : '#F1EFE8', color: m.role === 'user' ? '#FFFFFF' : '#444441' }}>
              {m.content}
            </div>
          ))}
          {loading && <div style={{ fontSize: 12, color: '#888780' }}><Spin size="small" /> {name}正在思考…</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <Input size="small" value={input} placeholder={'问' + name + '点别的…'}
          onChange={e => setInput(e.target.value)}
          onPressEnter={() => send()}
          style={{ borderRadius: 16, fontSize: 12.5 }} />
        <Button size="small" shape="circle" type="primary" icon={<SendOutlined />} onClick={() => send()} loading={loading} style={{ background: accent, borderColor: accent, flexShrink: 0 }} />
      </div>

      <VoiceCallModal open={callOpen} onClose={() => setCallOpen(false)} name={name}
        avatar={<img src={avatar} alt={name} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }} />}
        accentColor={accent} apiBase={apiBase}
        onResult={({ reply }) => { if (reply) setMsgs(prev => [...prev, { role: 'assistant', content: reply }]); }} />
    </div>
  );
}

function ConsultModal({ open, onClose }) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState([]); // [{name, accent, soft, content, error}]
  const run = async () => {
    const question = q.trim();
    if (!question || loading) return;
    setLoading(true); setAnswers([]);
    const results = await Promise.all(STAFF.map(async s => {
      try {
        const res = await chatOnce(s.apiBase, question);
        return { name: s.name, accent: s.accent, soft: s.soft, content: res.success ? res.reply : ('⚠️ ' + (res.error || '无响应')) };
      } catch (e) {
        return { name: s.name, accent: s.accent, soft: s.soft, content: '⚠️ 请求超时或失败，请重试' };
      }
    }));
    setAnswers(results);
    setLoading(false);
  };
  return (
    <Modal title={<span><TeamOutlined style={{ color: 'var(--brand)', marginRight: 8 }} />团队会诊 · 同一问题问三人</span>}
      open={open} onCancel={onClose} footer={null} width={760}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <Input value={q} placeholder="例：香蕉梳这个月还能不能加投？" onChange={e => setQ(e.target.value)} onPressEnter={run} allowClear />
        <Button type="primary" onClick={run} loading={loading} style={{ background: 'var(--brand)', borderColor: 'var(--brand)' }}>提问</Button>
      </div>
      {answers.length === 0 && !loading && <Empty description="输入问题，缇娜 / 河伯 / 小李会从各自视角一起回答" style={{ padding: 16 }} />}
      {loading && <div style={{ textAlign: 'center', padding: 24 }}><Spin /> 三位员工正在思考…</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {answers.map(a => (
          <div key={a.name} style={{ border: '1px solid #EFE7E0', borderRadius: 10, padding: 12, background: a.soft }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: a.accent, marginBottom: 6 }}>{a.name}</div>
            <div style={{ fontSize: 12.5, color: '#444441', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 220, overflowY: 'auto' }}>{a.content}</div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default function AiStaffPanel({ briefings }) {
  const [consultOpen, setConsultOpen] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: '#26201C' }}>AI 员工 · 今日值班</span>
        <span style={{ fontSize: 12.5, color: '#888780' }}>点卡片直接聊，不用跳页</span>
        <Button icon={<TeamOutlined />} onClick={() => setConsultOpen(true)} style={{ marginLeft: 'auto', background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff', borderRadius: 16 }}>团队会诊</Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
        {STAFF.map(s => (
          <StaffCard key={s.key} staff={s} briefing={(briefings || {})[s.key]} />
        ))}
      </div>
      <ConsultModal open={consultOpen} onClose={() => setConsultOpen(false)} />
    </div>
  );
}
